import { track } from "./analytics";
import { ANALYTICS_EVENTS } from "./events";

export type InAppBrowserName =
  | "instagram"
  | "facebook"
  | "kakaotalk"
  | "naver"
  | "line"
  | "android_webview"
  | "none";

export type KakaoLoginFailureStep =
  | "external_browser_handoff"
  | "server_callback"
  | "client_completion";

type LoginMethod = "kakao";

const PENDING_LOGIN_METHOD_STORAGE_KEY = "mypetroutine:pending-login-method:v1";
const PENDING_LOGIN_METHOD_TTL_MS = 10 * 60 * 1000;
const ERROR_REASON_MAX_LENGTH = 200;

export function resolveInAppBrowser(userAgent?: string | null): InAppBrowserName {
  const ua = userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");

  if (/instagram/i.test(ua)) {
    return "instagram";
  }
  if (/fban|fbav|fb_iab|fbios/i.test(ua)) {
    return "facebook";
  }
  if (/kakaotalk/i.test(ua)) {
    return "kakaotalk";
  }
  if (/naver\(inapp/i.test(ua)) {
    return "naver";
  }
  if (/\bline\//i.test(ua)) {
    return "line";
  }
  if (/android/i.test(ua) && /;\s*wv\)/i.test(ua)) {
    return "android_webview";
  }
  return "none";
}

export function trackKakaoLoginFailed(params: {
  step: KakaoLoginFailureStep;
  errorCode: string;
  errorReason?: string | null;
  userAgent?: string | null;
}) {
  track(ANALYTICS_EVENTS.KAKAO_LOGIN_FAILED, {
    step: params.step,
    error_code: params.errorCode,
    error_reason: params.errorReason ? params.errorReason.slice(0, ERROR_REASON_MAX_LENGTH) : null,
    in_app_browser: resolveInAppBrowser(params.userAgent),
  });
}

export function markPendingLoginMethod(method: LoginMethod, now = Date.now()) {
  try {
    window.sessionStorage.setItem(PENDING_LOGIN_METHOD_STORAGE_KEY, JSON.stringify({ method, at: now }));
  } catch {
    // Login method attribution is best-effort.
  }
}

export function clearPendingLoginMethod() {
  try {
    window.sessionStorage.removeItem(PENDING_LOGIN_METHOD_STORAGE_KEY);
  } catch {
    // Login method attribution is best-effort.
  }
}

export function consumePendingLoginMethod(now = Date.now()): LoginMethod | null {
  try {
    const raw = window.sessionStorage.getItem(PENDING_LOGIN_METHOD_STORAGE_KEY);
    window.sessionStorage.removeItem(PENDING_LOGIN_METHOD_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as { method?: unknown; at?: unknown };
    if (parsed.method !== "kakao" || typeof parsed.at !== "number") {
      return null;
    }
    return now - parsed.at <= PENDING_LOGIN_METHOD_TTL_MS ? parsed.method : null;
  } catch {
    return null;
  }
}
